/** Publish confirmation (spec §7): plain-words diff summary + in-flight
 * note. Also exports ConflictBanner (seeder re-merge failure surface). */
import { usePublishWorkflowConfig, type PublishBasis } from '@/hooks/useWorkflowConfig'
import { useWorkflowConfigStore } from '@/stores/workflowConfigStore'
import { extractPublishFindings } from './publishFindings'
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '@/components/ui'
import { overlayFieldLabel } from '@/utils/workflowConfigView'
import type { OverlayPathDescriptor } from '@/utils/workflowConfigView'
import { writeConflict, type WriteConflict } from '@/utils/writeConflict'
import type { CarryForwardRecord, ConfigFinding, OverlayDiffSummary } from '@/types/workflowConfig'

function DiffEntry({ groupName, refPath, titlePath, describe }: {
  groupName: string
  refPath: string
  titlePath?: string
  describe?: (path: string) => OverlayPathDescriptor
}) {
  const path = titlePath ?? refPath
  const d = describe?.(path) ?? { label: refPath, resolved: false }
  return (
    <li>
      {groupName}:{' '}
      <span title={path} className={d.resolved ? undefined : 'font-mono'}>
        {d.label}
      </span>
    </li>
  )
}

/** The overlay-vs-BASE list — the deployment's full customization picture
 * (pre-SA-238 this was the whole dialog body). */
function VsBaseList({ diff, describe }: {
  diff: OverlayDiffSummary
  describe?: (path: string) => OverlayPathDescriptor
}) {
  return (
    <ul className="text-sm text-foreground space-y-1">
      {(
        [
          ['Changed', diff.overridden],
          ['Added', diff.added],
          ['Disabled', diff.disabled],
        ] as const
      ).flatMap(([groupName, refs]) =>
        refs.map((ref) => (
          <DiffEntry key={`${groupName}:${ref}`} groupName={groupName} refPath={ref} describe={describe} />
        )),
      )}
      {diff.settings.map((key) => (
        <DiffEntry
          key={`settings:${key}`}
          groupName="Setting"
          refPath={key}
          titlePath={`settings.${key}`}
          describe={describe}
        />
      ))}
    </ul>
  )
}

/** SA-238 grouped body: the vs-live delta headlines; the full vs-base list
 * is demoted to a collapsed carried-forward group (omitted when nothing is
 * live yet). */
function VsLiveGroups({ diff, describe }: {
  diff: OverlayDiffSummary
  describe?: (path: string) => OverlayPathDescriptor
}) {
  const vsLive = diff.vs_live!
  const empty =
    vsLive.changed.length === 0 && vsLive.added.length === 0 && vsLive.removed.length === 0
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground">New in this publish</p>
      {empty ? (
        <p className="text-sm text-foreground">No changes vs the live version.</p>
      ) : (
        <ul className="text-sm text-foreground space-y-1">
          {(
            [
              ['Changed', vsLive.changed],
              ['Added', vsLive.added],
              ['Reverts to template default', vsLive.removed],
            ] as const
          ).flatMap(([groupName, refs]) =>
            refs.map((ref) => (
              <DiffEntry key={`${groupName}:${ref}`} groupName={groupName} refPath={ref} describe={describe} />
            )),
          )}
        </ul>
      )}
      {vsLive.has_live && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer">
            Already-live customizations carried forward
          </summary>
          <div className="mt-1">
            <VsBaseList diff={diff} describe={describe} />
          </div>
        </details>
      )}
    </div>
  )
}

const ORDER_PUBLISH_NOTE =
  'The new version takes effect for new conversations immediately. Conversations already in progress finish on the current version.'

export function PublishDialog({ workflowId, diff, describe, note = ORDER_PUBLISH_NOTE, basis, onPublished, onConflict, onClose }: {
  workflowId: string
  diff: OverlayDiffSummary
  describe?: (path: string) => OverlayPathDescriptor
  /** What a publish reaches — differs by kind (a process reads parameters
   * live, E2 spec D9). Defaults to the order sentence. */
  note?: string
  /** SA-416, SA-1410: what this publish is based on — the session's stamp, base and live version. */
  basis?: PublishBasis
  /** Leaves the caller's own edit session (the process store's); the order
   * store's session is always left below. */
  onPublished?: () => void
  /** A 409: the dialog closes and the bar raises its reload banner. */
  onConflict?: (conflict: WriteConflict) => void
  onClose: () => void
}) {
  const publish = usePublishWorkflowConfig(workflowId)
  const stopEditing = useWorkflowConfigStore((s) => s.stopEditing)
  const orderSessionId = useWorkflowConfigStore((s) => s.workflowId)

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose() }}>
      <ModalContent size="sm" showClose={false}>
        <ModalHeader>
          <ModalTitle>Publish this configuration?</ModalTitle>
        </ModalHeader>
        <ModalBody className="space-y-3">
          {diff.vs_live ? (
            <VsLiveGroups diff={diff} describe={describe} />
          ) : (
            <VsBaseList diff={diff} describe={describe} />
          )}
          <p className="text-xs text-muted-foreground">{note}</p>
          {publish.isError && (() => {
            const findings = extractPublishFindings(publish.error)
            return findings ? (
              <ul className="text-xs text-destructive space-y-0.5">
                {findings.map((f, i) => {
                  const d = describe?.(f.path) ?? { label: f.path, resolved: false }
                  return (
                    <li key={i}>
                      <span title={f.path} className={d.resolved ? undefined : 'font-mono'}>{d.label}</span>: {f.message}
                    </li>
                  )
                })}
              </ul>
            ) : (
              <p className="text-xs text-destructive">
                Publish failed — re-run validation and fix the findings first.
              </p>
            )
          })()}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={publish.isPending}
            isLoading={publish.isPending}
            onClick={() =>
              publish.mutate(basis, {
                onSuccess: () => {
                  // Sessions are global and survive navigation: only leave
                  // the order session when it is THIS workflow's.
                  if (orderSessionId === workflowId) stopEditing()
                  onPublished?.()
                  onClose()
                },
                onError: (error) => {
                  const refused = writeConflict(error)
                  if (refused && onConflict) {
                    onConflict(refused)
                    onClose()
                  }
                },
              })
            }
          >
            {publish.isPending ? 'Publishing…' : 'Publish'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

/** SA-84 Flow B: `onReview` (when given) adds a per-finding "Review" button
 * that deep-links into the editor at the finding's tab (page wires it via
 * `findingTarget`); `showPromoteCta` (when a `candidate`-tagged version
 * exists) adds a "Review & promote in Versions" button. Both are optional
 * so pre-existing callers/tests keep working unchanged. */
export function ConflictBanner({
  findings,
  onReview,
  showPromoteCta,
  onPromoteNav,
  describe,
}: {
  findings: ConfigFinding[]
  onReview?: (finding: ConfigFinding) => void
  showPromoteCta?: boolean
  onPromoteNav?: () => void
  describe?: (path: string) => OverlayPathDescriptor
}) {
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 space-y-2">
      <p className="text-sm font-medium text-destructive">
        A product update conflicts with your customization
      </p>
      <p className="text-xs text-destructive">
        This workflow keeps running on its last good version until the conflict is resolved.
      </p>
      <ul className="text-xs text-destructive space-y-0.5">
        {findings.map((f, i) => {
          const d = describe?.(f.path) ?? { label: f.path, resolved: false }
          return (
            <li key={i} className="flex items-center justify-between gap-2">
              <span>
                <span title={f.path} className={d.resolved ? undefined : 'font-mono'}>{d.label}</span>: {f.message}
              </span>
              {onReview && (
                <Button
                  size="sm"
                  variant="link"
                  className="h-auto shrink-0 p-0 text-xs text-destructive"
                  onClick={() => onReview(f)}
                >
                  Review
                </Button>
              )}
            </li>
          )
        })}
      </ul>
      {showPromoteCta && (
        <Button size="sm" variant="ghost" onClick={onPromoteNav}>
          Review &amp; promote in Versions
        </Button>
      )}
    </div>
  )
}

/** SA-84 Flow B: shown at page level only when a seeder carry-forward
 * happened AND there is no current merge error (an unresolved conflict
 * takes precedence — ConflictBanner covers that case). Informational, not
 * an error — border-border/bg-muted, never destructive styling. No
 * client-side dismiss: it clears itself once the operator ratifies
 * (edits/publishes) the draft, per the carry_forward contract. */
export function CarryForwardNotice({
  record,
  labelFor,
  onReview,
}: {
  record: CarryForwardRecord
  labelFor?: (key: string) => string
  onReview: () => void
}) {
  return (
    <div className="rounded-lg border border-border bg-muted p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-foreground">
          A product update carried {record.entries.length} of your customizations forward.
        </p>
        <Button
          size="sm"
          variant="link"
          className="h-auto shrink-0 p-0 text-xs"
          onClick={onReview}
        >
          Review
        </Button>
      </div>
      <ul className="text-xs text-muted-foreground space-y-0.5">
        {record.entries.map((e) => (
          <li key={e.key}>
            <span title={e.key}>{labelFor?.(e.key) ?? e.key}</span>
            {e.fields.length > 0 && <> — {e.fields.map(overlayFieldLabel).join(', ')}</>}
            {e.ask_languages.length > 0 && <> (asks: {e.ask_languages.join(', ')})</>}
          </li>
        ))}
      </ul>
    </div>
  )
}
